import manifest from "../data/landingTournament.json";
import config from "../config.js";

export const ASSIGNMENT_KEY = "lawlow.landingAssignment.v1";
const MAX_AGE = 30 * 24 * 60 * 60 * 1000;
const EVENTS = new Set(["assigned", "exposed", "cta"]);
export const PENDING_EVENT_PREFIX = "lawlow.landingPending.v1:";
const inFlight = new Map();
const REF_FIELDS = ["environment", "experimentId", "roundId", "variantId", "assignmentId", "visitorId", "assignedAt", "internal"];
let memoryAssignment;

export const isLandingDevHost = (hostname = window.location.hostname) =>
  ["localhost", "127.0.0.1", "front.lawlow.info"].includes(hostname);

const validBody = (body) => Array.isArray(body) && body.length >= 1 && body.length <= 8 &&
  body.every((section) => section && Object.keys(section).length === 2 &&
    typeof section.title === "string" && section.title.trim().length >= 1 && section.title.length <= 80 &&
    typeof section.text === "string" && section.text.trim().length >= 1 && section.text.length <= 2000);

export const validManifest = (value) => Boolean(
  value?.experimentId && value?.roundId && value.controlId === "main" &&
  Array.isArray(value.candidates) && value.candidates.length === 5 &&
  value.candidates.every((v) => v && typeof v.id === "string") &&
  new Set(value.candidates.map((v) => v.id)).size === 5 &&
  value.candidates.every((v) => v.id !== "main" && v.title && v.description &&
    ["problem", "preview", "situations", "steps", "clarity"].includes(v.layout) &&
    (v.body === undefined || validBody(v.body)))
);

// Tournament winners stay in candidate slots; main always uses its original content.
export const getLandingContent = (variantId, value = manifest) =>
  !validManifest(value) || variantId === "main" ? undefined
    : value.candidates.find((candidate) => candidate.id === variantId);

// Rejection sampling avoids modulo bias; all six slots have equal probability.
export const randomSlot = (count, cryptoSource = window.crypto) => {
  const limit = Math.floor(0x100000000 / count) * count;
  const bytes = new Uint32Array(1);
  do { cryptoSource.getRandomValues(bytes); } while (bytes[0] >= limit);
  return bytes[0] % count;
};

const randomId = () => Array.from(window.crypto.getRandomValues(new Uint8Array(16)),
  (b) => b.toString(16).padStart(2, "0")).join("");

export const getLandingPreview = (value = manifest) => {
  const params = new URLSearchParams(window.location.search);
  const host = window.location.hostname;
  const allowed = isLandingDevHost(host) ||
    (["lawlow.info", "www.lawlow.info"].includes(host) && params.get("internal_test") === "1");
  const preview = params.get("landing_preview");
  return allowed && validManifest(value) &&
    ["main", ...value.candidates.map((v) => v.id)].includes(preview) ? preview : null;
};

export const getLandingAssignment = (value = manifest) => {
  const dev = isLandingDevHost();
  const preview = getLandingPreview(value);
  if (preview) return { variantId: preview, measurable: false, preview: true };
  if (!validManifest(value) || (!dev && value.productionEnabled !== true)) {
    return { variantId: "main", measurable: false };
  }
  const ids = ["main", ...value.candidates.map((v) => v.id)];
  const params = new URLSearchParams(window.location.search);
  try {
    if (params.get("internal_test") === "1" ||
        window.sessionStorage.getItem("lawlow.internalMailTest") === "1") {
      return { variantId: ids[randomSlot(ids.length)], measurable: false, internal: true };
    }
    const key = `${ASSIGNMENT_KEY}:${dev ? "dev" : "prod"}`;
    const saved = JSON.parse(window.localStorage.getItem(key) || "null");
    const existing = saved || memoryAssignment;
    const now = Date.now();
    if (existing?.experimentId === value.experimentId && existing.roundId === value.roundId && ids.includes(existing.variantId) &&
        /^[a-f0-9]{32}$/.test(existing.assignmentId) && /^[a-f0-9]{32}$/.test(existing.visitorId) &&
        now - Date.parse(existing.assignedAt) >= 0 && now - Date.parse(existing.assignedAt) < MAX_AGE) {
      const assignment = {
        experimentId: existing.experimentId, roundId: existing.roundId,
        variantId: existing.variantId, assignmentId: existing.assignmentId,
        visitorId: existing.visitorId, assignedAt: existing.assignedAt,
        environment: dev ? "dev" : "prod", internal: false, measurable: true,
      };
      memoryAssignment = assignment;
      return assignment;
    }
    const assignment = {
      experimentId: value.experimentId,
      roundId: value.roundId,
      variantId: ids[randomSlot(ids.length)],
      assignmentId: randomId(),
      visitorId: /^[a-f0-9]{32}$/.test(existing?.visitorId) ? existing.visitorId : randomId(),
      assignedAt: new Date(now).toISOString(),
      environment: dev ? "dev" : "prod",
      internal: params.get("internal_test") === "1" ||
        window.sessionStorage.getItem("lawlow.internalMailTest") === "1",
      measurable: true,
    };
    // If persistent storage is unavailable, retain the original page and do not
    // create repeatedly re-randomized visitors in the experiment denominator.
    window.localStorage.setItem(key, JSON.stringify(assignment));
    memoryAssignment = assignment;
    return assignment;
  } catch (_) {
    return { variantId: "main", measurable: false };
  }
};

export const persistLandingCookie = (assignment) => {
  try {
    const domain = window.location.hostname.endsWith(".lawlow.info") ||
      window.location.hostname === "lawlow.info" ? "; Domain=lawlow.info" : "";
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    if (!assignment.measurable || assignment.internal) {
      const environment = isLandingDevHost() ? "dev" : "prod";
      document.cookie = `lawlow_landing_${environment}=; Path=/; Max-Age=0${domain}${secure}`;
      return;
    }
    const { measurable, ...data } = assignment;
    document.cookie = `lawlow_landing_${assignment.environment}=${encodeURIComponent(JSON.stringify(data))}; Path=/; Max-Age=2592000; SameSite=Lax${domain}${secure}`;
  } catch (_) {
    // This optional observation cookie never participates in authentication.
  }
};

const savedKey = (assignment, event) => `lawlow.landingEvent:${assignment.assignmentId}:${event}`;
const pendingKey = (assignment, event) => `${PENDING_EVENT_PREFIX}${assignment.environment}:${assignment.assignmentId}:${event}`;

// Replay only this browser's current assignment. Never replace its cookie or
// move an old receipt into another round, visitor, or observation time.
export const retryLandingEvents = (assignment) => {
  if (!assignment?.measurable || assignment.internal) return Promise.resolve(false);
  const key = `${assignment.environment}:${assignment.assignmentId}`;
  if (inFlight.has(key)) return inFlight.get(key);
  const delivery = (async () => {
    for (const event of EVENTS) {
      const pending = pendingKey(assignment, event);
      const stored = window.localStorage.getItem(pending);
      if (!stored) continue;
      if (window.localStorage.getItem(savedKey(assignment, event))) {
        window.localStorage.removeItem(pending);
        continue;
      }
      const payload = JSON.parse(stored);
      if (!payload || Object.keys(payload).some((field) => ![...REF_FIELDS, "event", "eventId", "occurredAt"].includes(field)) ||
          REF_FIELDS.some((field) => payload[field] !== assignment[field]) ||
          payload.event !== event || payload.eventId !== `${assignment.assignmentId}:${event}` ||
          !Number.isFinite(Date.parse(payload.occurredAt)) || Date.parse(payload.occurredAt) < Date.parse(assignment.assignedAt)) return false;
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 6000);
      let response;
      try {
        response = await fetch(`${config.serverUri}/public/landing-experiments/events`, {
          method: "POST", credentials: "include", keepalive: true,
          headers: { "Content-Type": "application/json" },
          body: stored, signal: controller.signal,
        });
      } finally {
        window.clearTimeout(timeout);
      }
      // A proxy's HTML 200 is not the server's persisted-event receipt.
      if (response.status !== 204) return false;
      window.localStorage.setItem(savedKey(assignment, event), new Date().toISOString());
      window.localStorage.removeItem(pending);
    }
    return true;
  })().catch(() => false).finally(() => inFlight.delete(key));
  inFlight.set(key, delivery);
  return delivery;
};

export const trackLandingEvent = async (event, assignment) => {
  if (!EVENTS.has(event) || !assignment?.measurable || assignment.internal) return false;
  try {
    if (window.localStorage.getItem(savedKey(assignment, event))) {
      void retryLandingEvents(assignment);
      return false;
    }
    const pending = pendingKey(assignment, event);
    if (!window.localStorage.getItem(pending)) {
      const data = Object.fromEntries(REF_FIELDS.map((field) => [field, assignment[field]]));
      const payload = { ...data, event, eventId: `${assignment.assignmentId}:${event}`,
        occurredAt: new Date().toISOString() };
      window.localStorage.setItem(pending, JSON.stringify(payload));
    }
    await retryLandingEvents(assignment);
    return Boolean(window.localStorage.getItem(savedKey(assignment, event)));
  } catch (_) {
    return false;
  }
};
